import { usePageContext } from '@portal/processes/Page/PageContext';
import { addParamToUrl } from '@portal/processes/DynamicLanders/utils';
import { useAtomValue } from 'jotai';
import { useRouter } from 'next/router';
import { useCallback } from 'react';
import { ShortLocaleEnum } from 'schema/locale';
import { getShortLocale } from 'shared/utils/locale';
import { offerSectionAtom } from '../../state/atoms/offerSectionAtom';
import { useStoreValue } from '../../state/hooks/useStore';
import { useLocale } from './useLocale';

type Options = {
  withScroll?: boolean;
  withRedirect?: boolean;
} & { bundleUrl?: string; buttonLink?: string };

export function useGoToOffersV2({
  withScroll = true,
  withRedirect = true,
  bundleUrl,
  buttonLink
}: Options) {
  const locale = useLocale();
  const language = getShortLocale(locale);
  const store = useStoreValue();
  const offerSectionAtomValue = useAtomValue(offerSectionAtom);
  const { getOfferCTALink, page } = usePageContext();
  const router = useRouter();

  const goToOffer = useCallback(() => {
    const offerBuilderCTA = getOfferCTALink();
    const scrollToOfferSection = withScroll && !!offerSectionAtomValue;

    if (scrollToOfferSection) {
      offerSectionAtomValue.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        inline: 'nearest'
      });
    } else if (withRedirect) {
      let newPath = offerBuilderCTA;
      if (store === 'us' && language === ShortLocaleEnum.en && bundleUrl) {
        newPath ||= bundleUrl;
      } else if (buttonLink) {
        newPath ||= buttonLink;
      }

      if (router.asPath.includes('partner') || router.asPath.includes('campaign')) {
        const splittedPathName = router.asPath.split('/');

        router.push(
          addParamToUrl(newPath || '', `lander=${splittedPathName[splittedPathName.length - 1]}`)
        );
      }

      if (page?.use_pdp && page?.pdp_url) {
        newPath = page.pdp_url;
      }

      if (newPath) {
        router.push(newPath);
      }
    }
  }, [
    router,
    withScroll,
    offerSectionAtomValue,
    withRedirect,
    getOfferCTALink,
    store,
    language,
    bundleUrl,
    buttonLink,
    page
  ]);

  return { goToOffer, locale };
}
